<template>
  <form ref="profile_form" @submit.prevent="handleSubmit">
    <base-input
      v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
      :label="`${$t('COMMON.ORGANIZATION')} (*)`"
      :placeholder="$t('COMMON.ORGANIZATION')"
    >
      <organization-selector
        :allowNone="true"
        :organization="salesInvoice.organization.id"
        :filterable="true"
        :showAll="false"
        :disabled="!!salesInvoice.id"
        @organizationChanged="onOrganizationChanged"
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.organization" />

    <base-input
      :label="`${$t('COMMON.LOCATION')}`"
      :placeholder="$t('COMMON.LOCATION')"
      v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOCATIONS)"
    >
      <locations-selector
        :locations="salesInvoice.allowedLocations"
        :filterable="true"
        :showAll="false"
        :multiple="true"
        :organization="salesInvoice.organization.id"
        @locationsChanged="
          (locations) => {
            salesInvoice.allowedLocations = locations;
            onFormChanged();
          }
        "
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.location" />

    <base-input
      :label="`${$t('COMMON.SERVICECENTER')}`"
      :placeholder="$t('COMMON.SERVICECENTER')"
      v-if="$currentUserCan($permissions.PERM_VIEW_ANY_SERVICECENTERS)"
    >
      <service-center-selector
        :serviceCenter="
          salesInvoice.serviceCenter ? salesInvoice.serviceCenter.id : null
        "
        :filterable="true"
        :showAll="false"
        :filterOrganization="salesInvoice.organization.id"
        @serviceCenterChanged="
          (serviceCenter) => {
            salesInvoice.serviceCenter.id = serviceCenter;
            salesInvoice.establishment.id = null;
            salesInvoice.cafeteria.id = null;
            salesInvoice.salesOrder.id = null;
            onFormChanged();
          }
        "
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.serviceCenter" />

    <base-input
      :label="`${$t('COMMON.ESTABLISHMENT')}`"
      :placeholder="$t('COMMON.ESTABLISHMENT')"
      v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
    >
      <establishment-selector :onlyActive="true"
        :establishment="
          salesInvoice.establishment ? salesInvoice.establishment.id : null
        "
        :filterable="true"
        :showAll="false"
        :filterOrganization="salesInvoice.organization.id"
        :filterServiceCenter="salesInvoice.serviceCenter.id"
        @establishmentChanged="onEstablishmentChange"
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.establishment" />

    <base-input
      :label="`${$t('COMMON.CAFETERIA')}`"
      :placeholder="$t('COMMON.CAFETERIA')"
      v-if="$currentUserCan($permissions.PERM_VIEW_ANY_CAFETERIAS)"
    >
      <cafeteria-selector
        :cafeteria="salesInvoice.cafeteria ? salesInvoice.cafeteria.id : null"
        :filterable="true"
        :showAll="false"
        :filterOrganization="salesInvoice.organization.id"
        :filterServiceCenter="salesInvoice.serviceCenter.id"
        :filterEstablishment="salesInvoice.establishment.id"
        @cafeteriaChanged="
          (cafeteria) => {
            salesInvoice.cafeteria.id = cafeteria;
            salesInvoice.salesOrder.id = null;
            onFormChanged();
          }
        "
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.cafeteria" />

    <base-input
      v-if="$currentUserCan($permissions.PERM_VIEW_ANY_SALES_ORDERS)"
      :label="`${$t('COMMON.SALES_ORDERS')}`"
      :placeholder="$t('COMMON.SALES_ORDERS')"
    >
      <sales-order-selector
        :allowNone="true"
        :salesOrder="
          salesInvoice.salesOrder ? salesInvoice.salesOrder.id : null
        "
        :filterable="true"
        :showAll="false"
        :disabled="!!salesInvoice.id"
        :filterOrganization="salesInvoice.organization.id"
        :filterStatus="ORDER_STATUS_VALIDATED"
        :filterServiceCenter="salesInvoice.serviceCenter.id"
        :filterEstablishment="salesInvoice.establishment.id"
        :filterCafeteria="salesInvoice.cafeteria.id"
        @salesOrderChanged="
          (salesOrderId, salesOrder) => {
            salesInvoice.salesOrder.id = salesOrderId;
            if (salesOrder) {
              salesInvoice.recipient.id = salesOrder.recipient.id;

              salesInvoice.billing_entity_type = salesOrder.billing_entity_type;
              salesInvoice.billing_firstname = salesOrder.billing_firstname;
              salesInvoice.billing_lastname = salesOrder.billing_lastname;
              salesInvoice.billing_company_name =
                salesOrder.billing_company_name;
              salesInvoice.billing_email = salesOrder.billing_email;
              salesInvoice.billing_country = salesOrder.billing_country;
              salesInvoice.billing_state = salesOrder.billing_state;
              salesInvoice.billing_city = salesOrder.billing_city;
              salesInvoice.billing_zipcode = salesOrder.billing_zipcode;
              salesInvoice.billing_address = salesOrder.billing_address;
              if (salesOrder.sourceWarehouse) {
                salesInvoice.sourceWarehouse.id = salesOrder.sourceWarehouse.id;
              }
              salesInvoice.paymentTerm = salesOrder.paymentTerm;
            }
            onFormChanged();
          }
        "
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.salesOrder" />

    <base-input
      :label="`${$t('SALES_INVOICES.EXPIRATION_TIME')} (*)`"
      :placeholder="$t('SALES_INVOICES.EXPIRATION_TIME')"
    >
      <flat-picker
        :config="{
          allowInput: true,
          minDate: 'today',
          locale: $flatPickrLocale(),
        }"
        class="form-control datepicker"
        v-model="salesInvoice.expiration_time"
        @on-change="
          () => {
            onFormChanged();
          }
        "
      >
      </flat-picker>
    </base-input>
    <validation-error :errors="apiValidationErrors.expiration_time" />

    <base-input :label="`${$t('INVOICES.INVOICING_DATE')} (*)`">
      <flat-picker
        :config="{
          allowInput: true,
          locale: $flatPickrLocale(),
        }"
        class="form-control datepicker"
        v-model="salesInvoice.invoicing_date"
        @on-change="
          () => {
            onFormChanged();
          }
        "
      >
      </flat-picker>
    </base-input>
    <validation-error :errors="apiValidationErrors.invoicing_date" />

    <base-input :label="`${$t(`SALES_INVOICES.SELECT_CUSTOMERS`)} (*)`">
      <customer-selector
        :allowNone="true"
        :customer="salesInvoice.recipient.id"
        :filterable="true"
        :showAll="false"
        :filterOrganization="salesInvoice.organization.id"
        :disabled="!!salesInvoice.id"
        @customerChanged="
          (customerId, customer) => {
            salesInvoice.recipient.id = customerId;
            if (customer) {
              salesInvoice.billing_entity_type = customer.customer_type;
              salesInvoice.billing_firstname = customer.firstname;
              salesInvoice.billing_lastname = customer.lastname;
              salesInvoice.billing_company_name = customer.company_name;
              salesInvoice.billing_email = customer.email;
              salesInvoice.billing_country = customer.country;
              salesInvoice.billing_state = customer.state;
              salesInvoice.billing_city = customer.city;
              salesInvoice.billing_zipcode = customer.zipcode;
              salesInvoice.billing_address = customer.address;
            }
            onFormChanged();
          }
        "
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.recipient" />

    <base-input :label="`${$t(`COMMON.SOURCE_WAREHOUSE`)}`">
      <warehouse-selector
        :allowNone="true"
        :warehouse="
          salesInvoice.sourceWarehouse ? salesInvoice.sourceWarehouse.id : null
        "
        :filterable="true"
        :showAll="false"
        :filterOrganization="salesInvoice.organization.id"
        :disabled="!!salesInvoice.id"
        @warehouseChanged="
          (warehouseId) => {
            salesInvoice.sourceWarehouse.id = warehouseId;
            onFormChanged();
          }
        "
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.sourceWarehouse" />

    <div class="mt-3">
      <base-checkbox
        v-model="salesInvoice.has_no_taxes"
        @change="onFormChanged"
        class="mb-3"
      >
        <span class="form-control-label">
          {{ $t("PURCHASES_INVOICES.EXCLUDING_TAXES") }}
        </span>
      </base-checkbox>
      <validation-error :errors="apiValidationErrors.has_no_taxes" />
    </div>

    <base-input :label="`${$t(`COMMON.PAYMENT_TERM`)} (*)`">
      <payment-term-selector
        :allowNone="false"
        :paymentTerm="
          salesInvoice.paymentTerm ? salesInvoice.paymentTerm.id : null
        "
        :filterable="true"
        :showAll="false"
        :filterOrganization="salesInvoice.organization.id"
        @paymentTermChanged="
          (paymentTermId) => {
            salesInvoice.paymentTerm.id = paymentTermId;
            onFormChanged();
          }
        "
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.paymentTerm" />

    <h2 class="mt-5">{{ $t("COMMON.NOTE") }}</h2>
    <html-editor v-model="salesInvoice.excerpt" @change="onFormChanged()">
    </html-editor>
    <validation-error :errors="apiValidationErrors.excerpt" />

    <h2 class="mt-5">{{ $t("COMMON.TERMS_CONDITIONS") }}</h2>
    <html-editor
      v-model="salesInvoice.terms_conditions"
      @change="onFormChanged()"
    >
    </html-editor>
    <validation-error :errors="apiValidationErrors.terms_conditions" />

    <h2 class="mt-5">{{ $t("COMMON.DESCRIPTION") }}</h2>
    <html-editor v-model="salesInvoice.description" @change="onFormChanged()">
    </html-editor>
    <validation-error :errors="apiValidationErrors.description" />

    <h2 class="mt-5">{{ $t("COMMON.BILLING_INFORMATIONS") }}</h2>
    <base-input
      :label="$t('COMMON.TYPE')"
      :placeholder="$t('COMMON.TYPE')"
      @change="onFormChanged"
    >
      <el-select name="Type" v-model="salesInvoice.billing_entity_type">
        <el-option
          :value="BILLING_ENTITY_TYPE_INDIVIDUAL"
          :label="$t('COMMON.BILLING_ENTITY_TYPE_INDIVIDUAL')"
        />
        <el-option
          :value="BILLING_ENTITY_TYPE_COMPANY"
          :label="$t('COMMON.BILLING_ENTITY_TYPE_COMPANY')"
        />
      </el-select>
    </base-input>
    <validation-error :errors="apiValidationErrors.billing_entity_type" />

    <base-input
      :label="$t('COMMON.COMPANY_NAME')"
      v-model="salesInvoice.billing_company_name"
      @change="onFormChanged"
      v-if="salesInvoice.billing_entity_type == BILLING_ENTITY_TYPE_COMPANY"
    />
    <validation-error :errors="apiValidationErrors.billing_company_name" />

    <div class="row">
      <div class="col">
        <base-input
          :label="$t('COMMON.FIRSTNAME')"
          v-model="salesInvoice.billing_firstname"
          @change="onFormChanged"
        />
        <validation-error :errors="apiValidationErrors.billing_firstname" />
      </div>
      <div class="col">
        <base-input
          :label="$t('COMMON.LASTNAME')"
          v-model="salesInvoice.billing_lastname"
        />
        <validation-error :errors="apiValidationErrors.billing_lastname" />
      </div>
    </div>

    <base-input
      :label="$t('COMMON.EMAIL')"
      v-model="salesInvoice.billing_email"
      @change="onFormChanged"
    />
    <validation-error :errors="apiValidationErrors.billing_email" />

    <div class="form-wrapper full mb-3">
      <addresses-selector
        @addressInputChangeChanged="
          (value) => {
            salesInvoice = {
              ...salesInvoice,
              billing_country: value.country,
              billing_state: value.state,
              billing_city: value.city,
              billing_city: value.city,
              billing_zipcode: value.zipcode,
              billing_address: value.address,
            };
            onFormChanged();
          }
        "
      />
    </div>

    <div class="row">
      <div class="col">
        <base-input
          :label="$t('COMMON.COUNTRY')"
          :placeholder="$t('COMMON.COUNTRY')"
        >
          <country-selector
            :country="salesInvoice.billing_country"
            :filterable="true"
            :showAll="false"
            @countryChanged="
              (country) => {
                salesInvoice.billing_country = country;
                onFormChanged();
              }
            "
          />
        </base-input>
        <validation-error :errors="apiValidationErrors.billing_country" />
      </div>
      <div class="col">
        <base-input
          :label="$t('COMMON.STATE')"
          :placeholder="$t('COMMON.STATE')"
        >
          <state-selector
            :country="salesInvoice.billing_country"
            :state="salesInvoice.billing_state"
            :filterable="true"
            :showAll="false"
            @stateChanged="
              (state) => {
                salesInvoice.billing_state = state;
                onFormChanged();
              }
            "
          />
        </base-input>
        <validation-error :errors="apiValidationErrors.billing_state" />
      </div>
      <div class="col">
        <base-input
          :label="$t('COMMON.CITY')"
          v-model="salesInvoice.billing_city"
          @change="onFormChanged"
        />
        <validation-error :errors="apiValidationErrors.billing_city" />
      </div>
      <div class="col">
        <base-input
          :label="$t('COMMON.ZIPCODE')"
          v-model="salesInvoice.billing_zipcode"
          @change="onFormChanged"
        />
        <validation-error :errors="apiValidationErrors.billing_zipcode" />
      </div>
    </div>

    <base-input
      :label="$t('COMMON.ADDRESS')"
      v-model="salesInvoice.billing_address"
      @change="onFormChanged"
    />
    <validation-error :errors="apiValidationErrors.billing_address" />

    <div class="my-4">
      <base-button
        type="button"
        class="btn btn-sm btn-primary"
        native-type="submit"
        :disabled="loading"
      >
        <i class="fas fa-spinner fa-spin" v-if="loading" />
        {{
          salesInvoice.id
            ? $t("SALES_INVOICES.EDIT_SALES_INVOICE")
            : $t("SALES_INVOICES.ADD_SALES_INVOICE")
        }}
      </base-button>
    </div>
  </form>
</template>
<script>
import moment from "moment";
import { cloneDeep } from "lodash";
import { Option, Select } from "element-ui";
import { mapGetters } from "vuex";
import swal from "sweetalert2";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import {
  BILLING_ENTITY_TYPE_COMPANY,
  BILLING_ENTITY_TYPE_INDIVIDUAL,
} from "@/constants/billingInformations";
import { ORDER_STATUS_VALIDATED } from "@/constants/orders";
import formMixin from "@/mixins/form-mixin";
import CountrySelector from "@/components/CountrySelector.vue";
import StateSelector from "@/components/StateSelector.vue";
import ValidationError from "@/components/ValidationError.vue";
import BaseInput from "@/components/Inputs/BaseInput.vue";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import SalesOrderSelector from "@/components/SalesOrderSelector.vue";
import LocationsSelector from "@/components/LocationsSelector.vue";
import ServiceCenterSelector from "@/components/ServiceCenterSelector.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";
import CafeteriaSelector from "@/components/CafeteriaSelector.vue";
import CustomerSelector from "@/components/CustomerSelector.vue";
import HtmlEditor from "@/components/Inputs/HtmlEditor";
import WarehouseSelector from "@/components/WarehouseSelector.vue";
import {
  ESTABLISHMENT_TYPE_ELEMENTARY,
  ESTABLISHMENT_TYPE_PROFESSIONAL,
  ESTABLISHMENT_TYPE_SECONDARY,
} from "@/constants/establishments";
import AddressesSelector from "@/components/AddressesSelector.vue";
import PaymentTermSelector from "@/components/PaymentTermSelector.vue";

export default {
  layout: "DashboardLayout",

  components: {
    BaseInput,
    ValidationError,
    AddressesSelector,
    OrganizationSelector,
    LocationsSelector,
    flatPicker,
    HtmlEditor,
    CustomerSelector,
    SalesOrderSelector,
    CountrySelector,
    StateSelector,
    ServiceCenterSelector,
    EstablishmentSelector,
    CafeteriaSelector,
    WarehouseSelector,
    PaymentTermSelector,
    [Select.name]: Select,
    [Option.name]: Option,
  },

  mixins: [formMixin],

  props: ["salesInvoiceData", "formErrors", "loading"],

  data() {
    return {
      salesInvoice: null,
      ORDER_STATUS_VALIDATED: ORDER_STATUS_VALIDATED,
      BILLING_ENTITY_TYPE_INDIVIDUAL: BILLING_ENTITY_TYPE_INDIVIDUAL,
      BILLING_ENTITY_TYPE_COMPANY: BILLING_ENTITY_TYPE_COMPANY,
      establishmentTypes: [
        ESTABLISHMENT_TYPE_PROFESSIONAL,
        ESTABLISHMENT_TYPE_SECONDARY,
        ESTABLISHMENT_TYPE_ELEMENTARY,
      ],
    };
  },

  created() {
    let salesInvoiceData = { ...this.salesInvoiceData };
    salesInvoiceData = this.$fillUserOrganizationData(salesInvoiceData);
    salesInvoiceData = this.$fillUserCafeteriasData(salesInvoiceData);
    if (!salesInvoiceData.id) {
      salesInvoiceData.terms_conditions =
        this.organizationConfig.DEFAULT_SALES_INVOICE_TERMS;
      salesInvoiceData.description =
        this.organizationConfig.DEFAULT_SALES_INVOICE_DESCRIPTION;
      salesInvoiceData.paymentTerm = {
        type: "payment-terms",
        id: this.organizationConfig.DEFAULT_SALES_PAYMENT_TERMS,
      };
    }
    salesInvoiceData.invoicing_date = salesInvoiceData.invoicing_date
      ? moment(salesInvoiceData.invoicing_date).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
    window.scrollTo({ top: 0, behavior: "smooth" });
    this.salesInvoice = salesInvoiceData;
  },

  computed: {
    ...mapGetters({
      organizationConfig: "organizationOptions/organizationConfig",
    }),
  },

  methods: {
    async handleSubmit() {
      let salesInvoiceData = cloneDeep(this.salesInvoice);
      salesInvoiceData.expiration_time = moment(
        salesInvoiceData.expiration_time
      ).toISOString();
      salesInvoiceData.invoicing_date = moment(
        salesInvoiceData.invoicing_date
      ).toISOString();
      salesInvoiceData = this.$fillUserOrganizationData(salesInvoiceData);
      salesInvoiceData = this.$fillUserCafeteriasData(salesInvoiceData);
      if (!salesInvoiceData.billing_company_name) {
        salesInvoiceData.billing_company_name = "N/A";
      }
      if (!salesInvoiceData.billing_firstname) {
        salesInvoiceData.billing_firstname = "N/A";
      }
      if (!salesInvoiceData.billing_lastname) {
        salesInvoiceData.billing_lastname = "N/A";
      }
      if (!salesInvoiceData.serviceCenter.id) {
        delete salesInvoiceData.serviceCenter;
      }
      if (!salesInvoiceData.establishment.id) {
        delete salesInvoiceData.establishment;
      }
      if (!salesInvoiceData.cafeteria.id) {
        delete salesInvoiceData.cafeteria;
      }
      this.$emit("salesInvoiceSubmitted", salesInvoiceData);
    },

    onEstablishmentChange(establishmentId, establishment) {
      this.salesInvoice.establishment.id = establishmentId;
      this.salesInvoice.has_no_taxes = this.establishmentTypes.includes(
        establishment.establishment_type
      );
      this.salesInvoice.cafeteria.id = null;
      this.salesInvoice.salesOrder.id = null;
      this.onFormChanged();
    },

    onFormChanged() {
      this.$emit("formChanged");
    },

    async onOrganizationChanged(organizationId) {
      if (!organizationId) {
        return;
      }
      swal.fire({
        allowEscapeKey: false,
        allowOutsideClick: false,
        onOpen: () => {
          swal.showLoading();
        },
      });
      await this.$store.dispatch(
        "organizationOptions/getOrganizationConfig",
        organizationId
      );
      this.salesInvoice.organization.id = organizationId;
      this.salesInvoice.allowedLocations = [];
      this.salesInvoice.recipient.id = null;
      this.salesInvoice.serviceCenter.id = null;
      this.salesInvoice.establishment.id = null;
      this.salesInvoice.cafeteria.id = null;
      this.salesInvoice.terms_conditions =
        this.organizationConfig.DEFAULT_SALES_INVOICE_TERMS;
      this.salesInvoice.description =
        this.organizationConfig.DEFAULT_SALES_INVOICE_DESCRIPTION;
      this.salesInvoice.paymentTerm = {
        type: "payment-terms",
        id: this.organizationConfig.DEFAULT_SALES_PAYMENT_TERMS,
      };
      this.onFormChanged();
      window.scrollTo({ top: 0, behavior: "smooth" });
      swal.close();
    },
  },

  watch: {
    formErrors(errors) {
      if (errors) {
        this.setApiValidation(errors);
      }
    },
    salesInvoiceData(salesInvoiceData) {
      if (salesInvoiceData) {
        this.salesInvoice = cloneDeep(this.salesInvoiceData);
        this.salesInvoice = this.$fillUserOrganizationData(this.salesInvoice);
        this.salesInvoice = this.$fillUserCafeteriasData(this.salesInvoice);
        this.salesInvoice.invoicing_date = this.salesInvoice.invoicing_date
          ? moment(this.salesInvoice.invoicing_date).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD");
        if (!this.salesInvoice.organization) {
          this.salesInvoice.organization = {
            type: "organizations",
            id: null,
          };
        }
        if (!this.salesInvoice.serviceCenter) {
          this.salesInvoice.serviceCenter = {
            type: "service-centers",
            id: null,
          };
        }
        if (!this.salesInvoice.establishment) {
          this.salesInvoice.establishment = {
            type: "establishments",
            id: null,
          };
        }
        if (!this.salesInvoice.cafeteria) {
          this.salesInvoice.cafeteria = {
            type: "cafeterias",
            id: null,
          };
        }
      }
    },
  },
};
</script>
